import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Avatar,
  Tooltip,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Chip,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsIcon from "@mui/icons-material/Settings";
import PersonIcon from "@mui/icons-material/Person";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import MenuIcon from "@mui/icons-material/Menu";

const PAGE_TITLES = {
  "/":                                        "Dashboard",
  "/add-customer":                            "Customers",
  "/add-truck":                               "Vehicles",
  "/add-device":                              "Solutions",
  "/technicians":                             "Technicians",
  "/invoice":                                 "Invoices",
  "/add-roles":                               "Roles",
  "/edit-profile":                            "Edit Profile",
  "/manage-permissions":                      "Permission Manager",
  "/device-history":                          "Device History",
  "/sales-new-installation":                  "Sales — New Installation",
  "/customer-support-new-installation":       "Customer Support — New Installation",
  "/customer-support-complaint":              "Customer Support — Complaint",
  "/customer-support-removal-transfer":       "Customer Support — Removal / Transfer",
  "/customer-support-repair-replacement":     "Customer Support — Repair / Replacement",
  "/technical-new-installation":              "Technical — New Installation",
  "/technical-complaint":                     "Technical — Complaint",
  "/technical-removal-transfer":              "Technical — Removal / Transfer",
  "/technical-repair-replacement":            "Technical — Repair / Replacement",
  "/production":                              "Production — New Installation",
  "/production-repair-replacement":           "Production — Repair / Replacement",
  "/dispatch":                                "Store — New Installation",
  "/dispatch-repair-replacement":             "Store — Repair / Replacement",
  "/sales-coordinator-new-installation":      "Collection Recovery — New Installation",
  "/sales-coordinator-removal-transfer":      "Collection Recovery — Removal / Transfer",
  "/sales-coordinator-repair-replacement":    "Collection Recovery — Repair / Replacement",
  "/installation-history":                    "Installation History",
};

const Navbar = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);

  const userRaw = localStorage.getItem("user");
  const user = userRaw ? JSON.parse(userRaw) : null;

  const initials = user?.name
    ? user.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()
    : "U";

  const pageTitle = PAGE_TITLES[location.pathname] ?? "";

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        background: "linear-gradient(180deg, #0b1e3d 0%, #0d2b5e 100%)",
        borderBottom: "1px solid rgba(255,255,255,0.07)",
        flexShrink: 0,
      }}
    >
      <Toolbar sx={{ minHeight: "52px !important", px: 2, gap: 1.5 }}>
        {/* Mobile menu toggle */}
        <IconButton
          size="small"
          onClick={onMenuClick}
          sx={{
            display: { xs: "flex", md: "none" },
            color: "rgba(255,255,255,0.75)",
            p: 0.75,
            "&:hover": { color: "#fff", background: "rgba(255,255,255,0.08)" },
          }}
        >
          <MenuIcon sx={{ fontSize: 20 }} />
        </IconButton>

        {/* Page title */}
        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.82)",
              fontSize: "0.82rem",
              fontWeight: 600,
              letterSpacing: "0.3px",
            }}
          >
            {pageTitle}
          </Typography>
        </Box>

        {/* Right section */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Department chip */}
          {user?.department && (
            <Chip
              label={user.department}
              size="small"
              sx={{
                background: "rgba(96,165,250,0.12)",
                color: "#93c5fd",
                border: "1px solid rgba(96,165,250,0.22)",
                fontSize: "0.68rem",
                height: 20,
                "& .MuiChip-label": { px: 1 },
              }}
            />
          )}

          {/* Username */}
          {user?.name && (
            <Typography
              sx={{
                color: "rgba(255,255,255,0.65)",
                fontSize: "0.75rem",
                whiteSpace: "nowrap",
                display: { xs: "none", sm: "block" },
              }}
            >
              {user.name}
            </Typography>
          )}

          {/* Avatar */}
          <Avatar
            sx={{
              width: 28,
              height: 28,
              fontSize: "0.7rem",
              fontWeight: 700,
              background: "linear-gradient(135deg, #1a4a8a, #60a5fa)",
              cursor: "default",
            }}
          >
            {initials}
          </Avatar>

          {/* Divider */}
          <Box
            sx={{
              width: "1px",
              height: 20,
              background: "rgba(255,255,255,0.12)",
              mx: 0.5,
            }}
          />

          {/* Settings */}
          <Tooltip title="Settings">
            <IconButton
              size="small"
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{
                color: "rgba(255,255,255,0.55)",
                p: 0.75,
                "&:hover": { color: "#fff", background: "rgba(255,255,255,0.08)" },
              }}
            >
              <SettingsIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          {/* Logout */}
          <Tooltip title="Logout">
            <IconButton
              size="small"
              onClick={handleLogout}
              sx={{
                color: "rgba(255,255,255,0.55)",
                p: 0.75,
                "&:hover": { color: "#f87171", background: "rgba(248,113,113,0.08)" },
              }}
            >
              <LogoutIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Toolbar>

      {/* Settings popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            background: "#0b1e3d",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: "10px",
            minWidth: 180,
            mt: 0.5,
            boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
          },
        }}
      >
        <List dense sx={{ py: 0.5 }}>
          <ListItem
            button
            component={Link}
            to="/add-roles"
            onClick={() => setAnchorEl(null)}
            sx={{
              borderRadius: "6px",
              mx: 0.5,
              "&:hover": { background: "rgba(255,255,255,0.08)" },
            }}
          >
            <ListItemIcon sx={{ color: "#60a5fa", minWidth: 30 }}>
              <ManageAccountsIcon sx={{ fontSize: 16 }} />
            </ListItemIcon>
            <ListItemText
              primary="Add Roles"
              primaryTypographyProps={{ sx: { color: "#fff", fontSize: "0.8rem" } }}
            />
          </ListItem>
          <ListItem
            button
            component={Link}
            to="/edit-profile"
            onClick={() => setAnchorEl(null)}
            sx={{
              borderRadius: "6px",
              mx: 0.5,
              "&:hover": { background: "rgba(255,255,255,0.08)" },
            }}
          >
            <ListItemIcon sx={{ color: "#60a5fa", minWidth: 30 }}>
              <PersonIcon sx={{ fontSize: 16 }} />
            </ListItemIcon>
            <ListItemText
              primary="Edit Profile"
              primaryTypographyProps={{ sx: { color: "#fff", fontSize: "0.8rem" } }}
            />
          </ListItem>
        </List>
      </Popover>
    </AppBar>
  );
};

export default Navbar;
